import { useState, useEffect, useCallback } from 'react';
import { TrendingUp, TrendingDown, ExternalLink, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { getInsightTrackRecord, deleteInsightPosition } from '../../services/api';

const ADMIN_EMAILS = ['dbai999@talkoru.com', 'dbai1089@talkoru.com'];

interface Position {
  id: string;
  ticker: string;
  direction: 'long' | 'short';
  entry_price: number;
  entry_date: string;
  current_price: number | null;
  price_date?: string;
  pnl_pct: number | null;
  days_held: number | null;
  insight_id: string;
  insight_title: string;
  rationale: string;
}

interface Summary {
  total_positions: number;
  avg_pnl_pct: number;
  win_rate: number;
  best: string;
  worst: string;
}

type SortKey = 'ticker' | 'pnl_pct' | 'days_held' | 'entry_date';

export default function InsightTrackRecordView() {
  const { currentUser } = useAuth();
  const [positions, setPositions] = useState<Position[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SortKey>('entry_date');
  const [sortAsc, setSortAsc] = useState(false);

  const isAdmin = currentUser?.email && ADMIN_EMAILS.includes(currentUser.email);

  const load = useCallback(async () => {
    try {
      const data = await getInsightTrackRecord();
      setPositions(data.positions || []);
      setSummary(data.summary || null);
    } catch {
      toast.error('Failed to load track record');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (posId: string, ticker: string) => {
    if (!currentUser) return;
    if (!window.confirm(`Remove ${ticker} from track record?`)) return;
    try {
      const token = await currentUser.getIdToken();
      await deleteInsightPosition(token, posId);
      setPositions(prev => prev.filter(p => p.id !== posId));
      toast.success(`Removed ${ticker}`);
    } catch {
      toast.error('Failed to delete');
    }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(key === 'ticker');
    }
  };

  const sorted = [...positions].sort((a, b) => {
    let cmp = 0;
    if (sortKey === 'ticker') cmp = a.ticker.localeCompare(b.ticker);
    else if (sortKey === 'pnl_pct') cmp = (a.pnl_pct ?? -999) - (b.pnl_pct ?? -999);
    else if (sortKey === 'days_held') cmp = (a.days_held ?? 0) - (b.days_held ?? 0);
    else if (sortKey === 'entry_date') cmp = a.entry_date.localeCompare(b.entry_date);
    return sortAsc ? cmp : -cmp;
  });

  const cardStyle = {
    background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
    padding: '16px 20px', textAlign: 'center' as const,
  };

  const headerBtn = (key: SortKey, label: string) => (
    <th
      key={key}
      onClick={() => handleSort(key)}
      style={{
        padding: '10px 12px', textAlign: 'left' as const, fontSize: '0.75rem',
        fontWeight: 600, color: '#6b7280', cursor: 'pointer', userSelect: 'none',
        borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap',
      }}
    >
      {label} {sortKey === key ? (sortAsc ? '↑' : '↓') : ''}
    </th>
  );

  if (loading) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: 24 }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: 16 }}>Insight Track Record</h2>
        <p style={{ color: '#9ca3af' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px', height: '100vh', overflow: 'auto', paddingBottom: 100 }}>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#111827', marginBottom: 4 }}>Insight Track Record</h2>
      <p style={{ fontSize: '0.85rem', color: '#6b7280', marginBottom: 20 }}>
        Performance of trade recommendations from market insights
      </p>

      {/* Summary cards */}
      {summary && summary.total_positions > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 24 }}>
          <div style={cardStyle}>
            <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: 4 }}>Positions</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827' }}>{summary.total_positions}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: 4 }}>Avg P&L</div>
            <div style={{
              fontSize: '1.4rem', fontWeight: 700,
              color: summary.avg_pnl_pct >= 0 ? '#16a34a' : '#dc2626',
            }}>
              {summary.avg_pnl_pct >= 0 ? '+' : ''}{summary.avg_pnl_pct}%
            </div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: 4 }}>Win Rate</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#111827' }}>
              {(summary.win_rate * 100).toFixed(0)}%
            </div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: 4 }}>Best</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#16a34a' }}>{summary.best}</div>
          </div>
          <div style={cardStyle}>
            <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginBottom: 4 }}>Worst</div>
            <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#dc2626' }}>{summary.worst}</div>
          </div>
        </div>
      )}

      {/* Positions table */}
      {positions.length === 0 ? (
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
          padding: 40, textAlign: 'center', color: '#9ca3af',
        }}>
          No positions tracked yet. Add positions from Market Insights using the "Track Record" button.
        </div>
      ) : (
        <div style={{
          background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb',
          overflow: 'hidden',
        }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ background: '#f9fafb' }}>
                  {headerBtn('ticker', 'Ticker')}
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Dir</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Entry</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Current</th>
                  {headerBtn('pnl_pct', 'P&L %')}
                  {headerBtn('days_held', 'Days')}
                  {headerBtn('entry_date', 'Date')}
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Rationale</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>Source</th>
                  {isAdmin && (
                    <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', borderBottom: '1px solid #e5e7eb', width: 40 }}></th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sorted.map((pos) => (
                  <tr key={pos.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#111827' }}>{pos.ticker}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        padding: '2px 8px', borderRadius: 4, fontSize: '0.75rem', fontWeight: 600,
                        background: pos.direction === 'long' ? '#f0fdf4' : '#fef2f2',
                        color: pos.direction === 'long' ? '#16a34a' : '#dc2626',
                      }}>
                        {pos.direction === 'long' ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                        {pos.direction.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      ${pos.entry_price?.toFixed(2)}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {pos.current_price != null ? `$${pos.current_price.toFixed(2)}` : '—'}
                    </td>
                    <td style={{
                      padding: '10px 12px', textAlign: 'right', fontWeight: 600,
                      fontFamily: 'monospace', fontSize: '0.8rem',
                      color: pos.pnl_pct == null ? '#9ca3af' : pos.pnl_pct >= 0 ? '#16a34a' : '#dc2626',
                    }}>
                      {pos.pnl_pct != null ? `${pos.pnl_pct >= 0 ? '+' : ''}${pos.pnl_pct.toFixed(2)}%` : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280', fontSize: '0.8rem' }}>
                      {pos.days_held ?? '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: '0.8rem' }}>
                      {pos.entry_date}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#6b7280', fontSize: '0.8rem', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {pos.rationale || '—'}
                    </td>
                    <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                      {pos.insight_id && (
                        <button
                          title={pos.insight_title || 'View source insight'}
                          onClick={() => {
                            localStorage.setItem('aiq_navigate_insight_id', pos.insight_id);
                            localStorage.setItem('aiq_active_view', 'MARKET_INSIGHTS');
                            window.dispatchEvent(new StorageEvent('storage', { key: 'aiq_active_view', newValue: 'MARKET_INSIGHTS' }));
                          }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#3b82f6', padding: 2 }}
                        >
                          <ExternalLink size={14} />
                        </button>
                      )}
                    </td>
                    {isAdmin && (
                      <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                        <button
                          onClick={() => handleDelete(pos.id, pos.ticker)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: '#d1d5db', padding: 2,
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#ef4444')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#d1d5db')}
                        >
                          <Trash2 size={14} />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {positions[0]?.price_date && (
            <div style={{ padding: '8px 12px', fontSize: '0.7rem', color: '#9ca3af', borderTop: '1px solid #f3f4f6' }}>
              Prices as of {positions[0].price_date}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
